import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import FormateCurrency from 'utils/FormateCurrency';
import { getAccountShortLabel } from 'utils/accountContext';
import { getAdminOverview } from 'api/user';

const moneyColor = (value) => (value < 0 ? '#BB4444' : '#95F205');

const StatCard = ({ label, value, color }) => (
  <Card sx={{ bgcolor: '#1c1e28', border: '1px solid #2a2d3a', height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Typography sx={{ color: '#9ca3af', fontSize: 13, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ color: color || '#fff', fontSize: { xs: 20, md: 24 }, fontWeight: 700 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const CategoryTable = ({ categories }) => (
  <Box sx={{ overflowX: 'auto', mt: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>Category</TableCell>
          <TableCell align="right" sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>Value</TableCell>
          <TableCell align="right" sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>Invested</TableCell>
          <TableCell align="right" sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>P&amp;L</TableCell>
          <TableCell align="right" sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>Trim</TableCell>
          <TableCell align="right" sx={{ color: '#9ca3af', borderColor: '#2a2d3a' }}>Assets</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {categories.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ color: '#6b7280', borderColor: '#2a2d3a' }}>
              No holdings
            </TableCell>
          </TableRow>
        ) : categories.map((row) => (
          <TableRow key={row.name}>
            <TableCell sx={{ color: '#fff', borderColor: '#2a2d3a' }}>{row.name}</TableCell>
            <TableCell align="right" sx={{ color: '#fff', borderColor: '#2a2d3a' }}>
              {FormateCurrency(row.value)}
            </TableCell>
            <TableCell align="right" sx={{ color: '#fff', borderColor: '#2a2d3a' }}>
              {FormateCurrency(row.invested)}
            </TableCell>
            <TableCell align="right" sx={{ color: moneyColor(row.profitLoss), borderColor: '#2a2d3a' }}>
              {FormateCurrency(row.profitLoss)}
            </TableCell>
            <TableCell align="right" sx={{ color: '#95F205', borderColor: '#2a2d3a' }}>
              {FormateCurrency(row.trimOpps)}
            </TableCell>
            <TableCell align="right" sx={{ color: '#fff', borderColor: '#2a2d3a' }}>
              {row.assetCount}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const AccountPanel = ({ account }) => (
  <Card sx={{ bgcolor: '#151821', border: '1px solid #2a2d3a', height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#248822',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            color: '#fff',
            fontSize: 14,
          }}
        >
          {getAccountShortLabel(account.name)}
        </Box>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
            {account.name}
          </Typography>
          <Typography sx={{ color: '#9ca3af', fontSize: 13 }}>
            {account.plan.planActive ? account.plan.name : `${account.plan.membershipLabel} · ${account.plan.name}`}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <StatCard label="Portfolio" value={FormateCurrency(account.portfolioTotal)} />
        </Grid>
        <Grid item xs={6}>
          <StatCard label="Invested" value={FormateCurrency(account.totalInvested)} />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            label="P&L"
            value={FormateCurrency(account.profitLoss)}
            color={moneyColor(account.profitLoss)}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard label="Trim Opps" value={FormateCurrency(account.trimOpps)} color="#95F205" />
        </Grid>
      </Grid>

      <Typography sx={{ color: '#fff', fontWeight: 600, mt: 3, mb: 0.5 }}>
        Category breakdown
      </Typography>
      <Typography sx={{ color: '#6b7280', fontSize: 12, mb: 1 }}>
        {account.assetCount} assets across {account.categories.length} categories
      </Typography>
      <CategoryTable categories={account.categories} />
    </CardContent>
  </Card>
);

const AdminOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminOverview()
      .then((res) => {
        setData(res.data);
        setError('');
      })
      .catch((err) => {
        setError(err.response?.data?.msg || 'Unable to load admin dashboard.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#95F205' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: '#f87171' }}>{error}</Typography>
      </Box>
    );
  }

  const { combined, accounts } = data;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography sx={{ color: '#fff', fontSize: { xs: 22, md: 28 }, fontWeight: 700, mb: 0.5 }}>
        Admin Dashboard
      </Typography>
      <Typography sx={{ color: '#9ca3af', mb: 3 }}>
        Combined totals across Matt A and Matt iTrust
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard label="Combined portfolio" value={FormateCurrency(combined.portfolioTotal)} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label="Combined invested" value={FormateCurrency(combined.totalInvested)} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            label="Combined P&L"
            value={FormateCurrency(combined.profitLoss)}
            color={moneyColor(combined.profitLoss)}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label="Trim opportunities" value={FormateCurrency(combined.trimOpps)} color="#95F205" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard label="Total assets" value={combined.assetCount} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {accounts.map((account) => (
          <Grid item xs={12} lg={6} key={account._id}>
            <AccountPanel account={account} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdminOverview;
